import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FaHome, FaCompass, FaQuestionCircle } from "react-icons/fa";
import PublicNavbar from "../Landing/PublicNavbar";
import PublicFooter from "../Landing/PublicFooter";
import {
  DoodleSparkle,
  DoodleUnderline,
  WashiTape,
  DoodlePaperclip,
} from "../../CustomUi/DoodleIcons";
import { usePageTitle } from "../../../hooks/usePageTitle";

const NotFoundPage: React.FC = () => {
  usePageTitle(
    "404 — Page Not Found",
    "The page you are looking for does not exist or has been moved. Explore Result by Numero school management."
  );
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="#FAF8F2" display="flex" flexDirection="column">
      <PublicNavbar />

      <Box
        flex="1"
        py={[16, 24]}
        className="notebook-dots"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
      >
        <Container maxW="2xl" position="relative" zIndex={1}>
          <VStack gap={8} textAlign="center">
            {/* Main Notebook Index Card */}
            <Box
              w="full"
              bg="white"
              p={[8, 12]}
              borderRadius="2xl"
              border="3px solid #0A3D2E"
              boxShadow="7px 9px 0px #0A3D2E"
              position="relative"
              transform="rotate(-0.5deg)"
            >
              {/* Washi Tape Accent */}
              <WashiTape
                angle={-2}
                top="-14px"
                left="50%"
                width="180px"
                transform="translateX(-50%)"
                tapeColor="#FEF08A"
                label="HALL PASS EXPIRED 🎒"
              />

              {/* Paperclip */}
              <DoodlePaperclip
                color="#10B981"
                size={40}
                position="absolute"
                top="-18px"
                right="32px"
              />

              <VStack gap={6} pt={4}>
                {/* 404 Big Display */}
                <Box position="relative" display="inline-block">
                  <Heading
                    as="h1"
                    fontSize={["6xl", "8xl"]}
                    fontWeight="extrabold"
                    color="brand.900"
                    lineHeight="1"
                    letterSpacing="-0.04em"
                  >
                    404
                  </Heading>
                  <DoodleUnderline
                    position="absolute"
                    bottom="-12px"
                    left="0"
                    color="#F59E0B"
                    strokeWidth={4}
                    height="18px"
                  />
                </Box>

                {/* Sparkling Sub-badge */}
                <HStack gap={2} justify="center">
                  <DoodleSparkle size={18} color="#F59E0B" />
                  <Text
                    className="font-handwriting"
                    fontSize={["xl", "2xl"]}
                    color="amber.800"
                    fontWeight="bold"
                  >
                    Did you take a wrong turn at the school library? 📚
                  </Text>
                  <DoodleSparkle size={18} color="#F59E0B" />
                </HStack>

                <VStack gap={2}>
                  <Heading
                    as="h2"
                    fontSize={["xl", "2xl"]}
                    fontWeight="bold"
                    color="gray.800"
                  >
                    Looks like this page wandered off school grounds.
                  </Heading>
                  <Text fontSize="sm" color="gray.600" maxW="md" lineHeight="1.7">
                    The bell rang and this page wasn&apos;t in its seat! It might have been
                    moved, renamed, or never existed in the first place.
                  </Text>
                </VStack>

                {/* Navigation CTA options */}
                <VStack gap={3} w="full" pt={3}>
                  <HStack gap={4} flexWrap="wrap" justify="center" w="full">
                    <Button
                      size="lg"
                      bg="brand.800"
                      color="white"
                      px={7}
                      fontWeight="bold"
                      borderRadius="xl"
                      border="2px solid #06231A"
                      boxShadow="3px 3px 0px #06231A"
                      transition="all 0.15s ease"
                      _hover={{
                        bg: "brand.900",
                        transform: "translate(-2px, -2px)",
                        boxShadow: "5px 5px 0px #06231A",
                      }}
                      onClick={() => navigate("/")}
                    >
                      <Icon as={FaHome} mr={2} /> Back to Class (Home)
                    </Button>

                    <Button
                      size="lg"
                      variant="outline"
                      borderColor="#0A3D2E"
                      color="#0A3D2E"
                      bg="white"
                      px={7}
                      fontWeight="bold"
                      borderRadius="xl"
                      border="2px solid #0A3D2E"
                      boxShadow="3px 3px 0px #0A3D2E"
                      transition="all 0.15s ease"
                      _hover={{
                        bg: "brand.50",
                        transform: "translate(-2px, -2px)",
                        boxShadow: "5px 5px 0px #0A3D2E",
                      }}
                      onClick={() => navigate("/features")}
                    >
                      <Icon as={FaCompass} mr={2} /> Explore Features
                    </Button>
                  </HStack>

                  <Button
                    variant="ghost"
                    size="sm"
                    color="gray.600"
                    fontSize="xs"
                    fontWeight="medium"
                    _hover={{ color: "brand.800", bg: "transparent" }}
                    onClick={() => navigate("/contact")}
                  >
                    <Icon as={FaQuestionCircle} mr={1.5} /> Need help? Contact Support
                  </Button>
                </VStack>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <PublicFooter />
    </Box>
  );
};

export default NotFoundPage;
